import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { Controller, useFormContext } from 'react-hook-form';

import {
  EditorableSchema,
  FieldSchemaEditorSupportedTypes,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import {
  ConfigSection,
  RequireCheckboxContainer,
} from 'dashboard/components-lib/FieldSchemaEditorForm/shared';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import {
  FieldSchemaArray,
  FieldSchemaBoolean,
  FieldSchemaChoices,
  FieldSchemaDate,
  FieldSchemaFile,
  FieldSchemaHash,
  FieldSchemaInteger,
  FieldSchemaMultiChoices,
  FieldSchemaNumber,
  FieldSchemaString,
  FieldSchemaType,
} from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const hasRequirePresenceConfig = (
  schema: FieldSchemaEditorSupportedTypes,
): schema is
  | FieldSchemaArray
  | FieldSchemaBoolean
  | FieldSchemaChoices
  | FieldSchemaDate
  | FieldSchemaFile
  | FieldSchemaHash
  | FieldSchemaInteger
  | FieldSchemaMultiChoices
  | FieldSchemaNumber
  | FieldSchemaString => {
  return (
    schema.type === FieldSchemaType.Array ||
    schema.type === FieldSchemaType.Boolean ||
    schema.type === FieldSchemaType.Choices ||
    schema.type === FieldSchemaType.Date ||
    schema.type === FieldSchemaType.File ||
    schema.type === FieldSchemaType.Hash ||
    schema.type === FieldSchemaType.Integer ||
    schema.type === FieldSchemaType.MultiChoices ||
    schema.type === FieldSchemaType.Number ||
    schema.type === FieldSchemaType.String
  );
};

interface RequirePresenceControlsProps {
  fieldSchema: EditorableSchema;
  fieldName: string;
  formContext: ReturnType<typeof useFormContext>;
  canEdit: (fieldSchema: EditorableSchema) => boolean;
}

const RequirePresenceControls: React.FC<RequirePresenceControlsProps> = ({
  fieldSchema,
  fieldName,
  formContext,
  canEdit,
}) => {
  const { control } = formContext;
  const isRequirePresenceEnabled = useFeatureFlag('dashboard:txn-additional-field-validations');

  if (!isRequirePresenceEnabled || !hasRequirePresenceConfig(fieldSchema)) return <></>;

  return (
    <>
      <Spacer height={2} />
      <ConfigSection>
        <Spacer height={2} />
        <Heading size='sm'>Configurations</Heading>
        <Spacer height={2} />
        <Paragraph>
          Changing these configurations will only affect new transactions created
        </Paragraph>
        <Spacer height={2} />
        <RequireCheckboxContainer>
          <Controller
            name={`${fieldName}.config.required`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                label={
                  fieldSchema.type === FieldSchemaType.Array ||
                  fieldSchema.type === FieldSchemaType.MultiChoices
                    ? 'Require at least one item'
                    : 'Require field'
                }
                value={value}
                checked={value}
                disabled={!canEdit(fieldSchema)}
                onChange={onChange}
              />
            )}
          />
          <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
            <Paragraph>
              Required fields must be populated for the transaction to be created
            </Paragraph>
          </Tooltip>
        </RequireCheckboxContainer>
      </ConfigSection>
    </>
  );
};

export default RequirePresenceControls;
